import React, { useState, forwardRef, useImperativeHandle, useContext } from "react";
import { Button, Col, Divider, Modal, Space, Table, Tag, Typography } from "antd";
const { Text } = Typography;

import { extractDepthClassifications, formatCurrency, formatToTwoDecimalPlaces, TextForInvoicePosition, TextForTransactionState } from "../../utils";

import { currencyGetter } from "../../../../utils/BankCurrencyUtils";
import { InvoicesTypes, InvoicesTypesColor } from "../../../../constants";
import { formatNumberOfCurrency, formatShowStandarDate } from "../../../../utils/Formaters";

import PropTypes from "prop-types";
import { ClassificationsContext } from "../../../../contexts/ClassificationsContext";

// ! =======================================================================
// ! =======================================================================

export const ModalTransactionDetails = forwardRef(({ currency }, ref) => {
	const [transactionToView, setTransactionToView] = useState({});

	const { classifications } = useContext(ClassificationsContext);
	const classificationOptions = extractDepthClassifications(classifications);
	const classificationName = classificationOptions.find(opt => opt.value === transactionToView?.classification_id)?.label;

	useImperativeHandle(ref, () => ({
		openModal: (transactionData) => setTransactionToView(transactionData),
	}));
	
	return (
		<Modal
			title={`Transacción ${transactionToView?.transaction_number}`}
			open={JSON.stringify(transactionToView) !== "{}"}
			onCancel={() => setTransactionToView({})}
			footer={() => (
				<Space>
					<Button
						type="primary"
						onClick={() => setTransactionToView({})}
					>
						Ok
					</Button>
				</Space>
			)}
		>
			<Divider orientation="left">Detalles:</Divider>
			<Col>
				<Text strong>Nota: </Text>
				<Text>{transactionToView?.note}</Text>
			</Col>
			<br />
			<Col>
				<Text strong>Descripción: </Text>
				<Text>{transactionToView?.description}</Text>
			</Col>
			<br />
			<Col>
				<Text strong>Estado: </Text>
				<Tag color={TextForTransactionState[transactionToView?.state]?.color}>
					{TextForTransactionState[transactionToView?.state]?.label}
				</Tag>
			</Col>
			<br />
			<Col>
				<Text strong>Clasificación: </Text>
				<Text>
					{!classificationName ? "Clasificación No Insertada" : classificationName}
				</Text>
			</Col>								

			<Divider orientation="left">Estado Reconciliación Bancaria:</Divider>
			<Col>
				<Text strong>Saldo: </Text>
				<Text>{`${currencyGetter[currency]} ${formatCurrency(transactionToView?.amount)}`}</Text>
			</Col>
			<br />
			<Col>
				<Text strong>Porcentaje Transacción Consumida: </Text>
				<Text>
					{`${((formatToTwoDecimalPlaces(Object.values(transactionToView?.reconciliation?.invoices ?? {}).reduce(
						(accumulator, item) => accumulator += item.amount, 0
					)) / formatToTwoDecimalPlaces(transactionToView?.amount)) * 100)} %`}
				</Text>
			</Col>
			<br />
			<Col>
				<Text strong>Total Facturas Reconciladas: </Text>
				<Text>{Object.values(transactionToView?.reconciliation?.invoices ?? {}).length} Factura(s)</Text>
			</Col>
			<br />
			<Text strong>Estado Consumo: </Text>
			<Text type={`${transactionToView?.reconciliation?.transaction_consumed ? "success" : "warning"}`}>
				{transactionToView?.reconciliation?.transaction_consumed ? "Transacción consumida por completo" : "Transacción no consumida"}
			</Text>				
			<br />
		</Modal>
	);
});

ModalTransactionDetails.displayName = "ModalTransactionDetails";

ModalTransactionDetails.propTypes = {
	currency: PropTypes.string,	
};

// ! =======================================================================
// ! =======================================================================

const findAccountNameById = (accountingPlan, accountId) => {
	// Buscar el elemento en accountingPlan que tenga un id que coincida con accountId
	const matchingElement = accountingPlan.find(planItem => planItem.id.toString() === accountId.toString());
  
	// Si se encuentra un elemento coincidente, retorna su propiedad 'name'
	if (matchingElement) {
		if (!matchingElement.display_name) {
			return matchingElement.name;
		}
		return matchingElement.display_name;
	} else {
		// Si no se encuentra ninguna coincidencia, retorna null o un mensaje indicativo
		return null; // o "No matching account found"
	}
};

export const ModalInvoiceDetails = forwardRef(({company = {}}, ref) => {
	const [selectedInvoiceToView, setSelectedInvoiceToView] = useState({});
	
	useImperativeHandle(ref, () => ({
		openModal: (transactionData) => setSelectedInvoiceToView(transactionData),
	}));

	return (
		<Modal
			title={`Factura ${selectedInvoiceToView?.reference} tipo ${TextForInvoicePosition[selectedInvoiceToView?.position]?.label}`}
			open={JSON.stringify(selectedInvoiceToView) !== "{}"}
			onCancel={() => setSelectedInvoiceToView({})}
			footer={() => (
				<Space>
					<Button
						type="primary"
						onClick={() => setSelectedInvoiceToView({})}
					>
						Ok
					</Button>
				</Space>
			)}
			width='70vw'
		>
			{/* // TODO: Use the InvoiceView Component or similar for show the data. */}
			<Divider orientation="center">{selectedInvoiceToView?.sender_name ? "Emisor" : "Receptor"} :</Divider>
			<Text>{selectedInvoiceToView?.receptor_name ?? selectedInvoiceToView?.sender_name ?? "N/A"}</Text>

			<Divider orientation="center">Especificaciones Contables:</Divider>
			<Col>
				<Text strong>Fecha: </Text>
				<Text>{formatShowStandarDate(selectedInvoiceToView?.accounting_specifications?.accounting_date)}</Text>
			</Col>
			<br />
			<Col>
				<Text strong>Descripción: </Text>
				<Text>{selectedInvoiceToView?.accounting_specifications?.description}</Text>
			</Col>
			<br />
			<Col>
				<Text strong>Diario: </Text>				
				<Text>{findAccountNameById(
					company.accounting_resources.diaries,
					selectedInvoiceToView?.accounting_specifications?.diary ?? ""
				)}</Text>
			</Col>
			<br />
			<Col>
				<Text strong>Tipo de {selectedInvoiceToView?.position === "in" ? "Venta" : "Compra"}: </Text>
				<Text>{selectedInvoiceToView?.accounting_specifications?.[`${selectedInvoiceToView?.position === "in" ? "sale_type" : "expense_type"}`] ?? ""}</Text>
			</Col>
			<br />
			<Col>
				<Text strong>Estado: </Text>								
				<Tag 
					color={InvoicesTypesColor[selectedInvoiceToView?.accounting_specifications?.state] ?? "blue"}
				>
					{InvoicesTypes[selectedInvoiceToView?.accounting_specifications?.state]}
				</Tag>
			</Col>
			<br />

			<Divider orientation="center">Items:</Divider>
			{selectedInvoiceToView?.items && (
				<Table
					size="small"
					style={{
						width: "100%",
					}}
					pagination={false}
					bordered={false}
					columns={[
						{
							title: "Cantidad",
							dataIndex: "quantity",
							key: "quantity",
							render: (text) => (
								<div style={{ width: "60px" }}>{text}</div>
							),
						},
						{
							title: "Tipo",
							dataIndex: "type",
							key: "type",
							render: (text) => (								
								<div style={{ width: "30px" }}>{text}</div>
							),
						},
						{
							title: "Descripción Inicial",
							dataIndex: "description",
							key: "description",
						},
						{
							title: "Clasificación",
							dataIndex: "accounting_classification",
							key: "accounting_classification",
							render: (value) => (
								<Text>{findAccountNameById(
									company.accounting_resources.accounting_plan,
									selectedInvoiceToView.items.find((item) => item.line === value[1])
										.accounting_specifications[selectedInvoiceToView.position].accounting_classification
								)}</Text>
							),
						},
						{
							title: "Analítica",
							dataIndex: "analytic_classification",
							key: "analytic_classification",
							render: (value) => (
								<Text>{findAccountNameById(
									company.accounting_resources.others.analytic_accounts,
									selectedInvoiceToView.items.find((item) => item.line === value[1])
										.accounting_specifications[selectedInvoiceToView.position].analytic_classification
								) ?? "N/A"}</Text>
							),
						},
						{
							title: "Total",
							dataIndex: "total",
							key: "total",
							render: (text) => (
								<div style={{ width: "100px" }}>{text}</div>
							),
						},
						{
							title: "Impuestos",
							dataIndex: "taxes",
							key: "taxes",
							render: (taxes) => (
								<>
									{taxes.length === 0 && (
										<div>Este producto no tiene impuestos</div>
									)}
									{taxes.length !== 0 &&
										taxes.map((tax) => (
											<Tag color="blue" key={tax.name}>
												{tax.name +
													" = " +
													formatNumberOfCurrency(
														tax.total,
														selectedInvoiceToView.currency !== ""
															? selectedInvoiceToView.currency
															: "GTQ"
													)}
											</Tag>
										))}
								</>
							),
						},
					]}
					dataSource={selectedInvoiceToView.items.map((item, index) => ({
						key: index,
						quantity: item.quantity,
						type: item.type,
						description: item.initial_description,
						accounting_classification: [
							item.accounting_classification,
							item.line,
						],
						analytic_classification: [
							item.analytic_classification,
							item.line
						],
						total: formatNumberOfCurrency(
							item.total,
							selectedInvoiceToView.currency !== "" ? selectedInvoiceToView.currency : "GTQ"
						),
						taxes: item.taxes,
					}))}
				/>
			)}

			<Divider orientation="center">Estado Reconciliación Bancaria:</Divider>
			<Col>
				<Text strong>Total Facturas Reconciladas: </Text>
				<Text>{Object.values(selectedInvoiceToView?.reconciliation?.[selectedInvoiceToView?.position] ?? {}).length} Transacción(es)</Text>
			</Col>
		</Modal>
	);
});

ModalInvoiceDetails.displayName = "ModalInvoiceDetails";

ModalInvoiceDetails.propTypes = {
	company: PropTypes.object.isRequired,
};
